import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { ChevronDown } from 'lucide-react'
import { uiScope } from '@/lib/cssScope'
import styles from '@/styles/components/ui/Select.module.css'

export interface SelectOption<T extends string | number = string | number> {
  value: T
  label: string
  disabled?: boolean
}

export interface SelectProps<T extends string | number> {
  /** Uppercase label above the trigger (e.g. SCANNER). */
  label?: ReactNode
  options: SelectOption<T>[]
  value: T | null
  onChange: (value: T | null) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  /** Minimum width for the trigger (e.g. `10rem`, `200px`). */
  minWidth?: string
  /** Visual variant for the trigger button. */
  variant?: 'default' | 'outline'
  /** Passed to the root for tests / layout. */
  'data-testid'?: string
}

function findIndex<T extends string | number>(options: SelectOption<T>[], value: T | null) {
  if (value === null) return -1
  return options.findIndex((o) => o.value === value && !o.disabled)
}

export function Select<T extends string | number>({
  label,
  options,
  value,
  onChange,
  placeholder = '',
  disabled = false,
  className = '',
  minWidth,
  variant = 'default',
  'data-testid': dataTestId,
}: SelectProps<T>) {
  const uid = useId()
  const labelId = `${uid}-label`
  const listboxId = `${uid}-listbox`
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)

  const enabledIndices = useMemo(
    () => options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0),
    [options],
  )

  const isEmpty = options.length === 0
  const allDisabled = options.length > 0 && enabledIndices.length === 0
  const isDisabled = disabled || isEmpty || allDisabled

  const selectedIndex = useMemo(() => findIndex(options, value), [options, value])

  const displayLabel = useMemo(() => {
    if (value === null || selectedIndex < 0) return null
    return options[selectedIndex]?.label ?? null
  }, [options, selectedIndex, value])

  const getNextEnabledIndex = useCallback(
    (current: number, direction: 1 | -1) => {
      if (enabledIndices.length === 0) return 0
      const posInEnabled = enabledIndices.indexOf(current)
      const start = posInEnabled >= 0 ? posInEnabled : 0
      const nextPos = (start + direction + enabledIndices.length) % enabledIndices.length
      return enabledIndices[nextPos]!
    },
    [enabledIndices],
  )

  const openMenu = useCallback(() => {
    if (isDisabled || enabledIndices.length === 0) return
    setOpen(true)
    const start =
      selectedIndex >= 0 && !options[selectedIndex]?.disabled
        ? selectedIndex
        : enabledIndices.length > 0
          ? enabledIndices[0]!
          : 0
    setHighlightedIndex(start)
  }, [enabledIndices, isDisabled, options, selectedIndex])

  const closeMenu = useCallback(() => {
    setOpen(false)
  }, [])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const root = containerRef.current
      if (root && !root.contains(e.target as Node)) closeMenu()
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open, closeMenu])

  useEffect(() => {
    if (!open || !menuRef.current) return
    const el = menuRef.current.querySelector<HTMLElement>(`[data-option-index="${highlightedIndex}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex, open])

  useEffect(() => {
    if (!open) return
    const id = requestAnimationFrame(() => menuRef.current?.focus())
    return () => cancelAnimationFrame(id)
  }, [open])

  const selectIndex = useCallback(
    (index: number) => {
      const opt = options[index]
      if (!opt || opt.disabled) return
      onChange(opt.value)
      closeMenu()
    },
    [closeMenu, onChange, options],
  )

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (isDisabled) return
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) openMenu()
      else {
        const dir: 1 | -1 = e.key === 'ArrowDown' ? 1 : -1
        setHighlightedIndex((prev) => getNextEnabledIndex(prev, dir))
      }
      return
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (!open) openMenu()
      else selectIndex(highlightedIndex)
      return
    }
    if (e.key === 'Escape' && open) {
      e.preventDefault()
      closeMenu()
    }
  }

  const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) => getNextEnabledIndex(prev, 1))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => getNextEnabledIndex(prev, -1))
      return
    }
    if (e.key === 'Home') {
      e.preventDefault()
      if (enabledIndices.length) setHighlightedIndex(enabledIndices[0]!)
      return
    }
    if (e.key === 'End') {
      e.preventDefault()
      if (enabledIndices.length) setHighlightedIndex(enabledIndices[enabledIndices.length - 1]!)
      return
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      selectIndex(highlightedIndex)
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      closeMenu()
    }
  }

  const rootClass = [uiScope('select'), styles.root, className].filter(Boolean).join(' ')

  return (
    <div ref={containerRef} className={rootClass} data-testid={dataTestId}>
      {label != null && label !== false && (
        <span id={labelId} className={styles.label}>
          {label}
        </span>
      )}
      <button
        type="button"
        className={`${styles.trigger} ${variant === 'outline' ? styles.triggerOutline : ''} ${open ? styles.triggerOpen : ''}`}
        style={minWidth ? { minWidth } : undefined}
        disabled={isDisabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-labelledby={label != null && label !== false ? labelId : undefined}
        aria-label={label == null || label === false ? String(placeholder || 'Select') : undefined}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={`${styles.triggerText} ${displayLabel == null ? styles.placeholder : ''}`}>
          {displayLabel ?? placeholder}
        </span>
        <ChevronDown className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} aria-hidden />
      </button>

      {open && (
        <div
          ref={menuRef}
          id={listboxId}
          role="listbox"
          tabIndex={0}
          aria-activedescendant={`${listboxId}-opt-${highlightedIndex}`}
          className={styles.menu}
          onKeyDown={onMenuKeyDown}
        >
          {options.map((opt, index) => {
            const selected = value !== null && opt.value === value
            const highlighted = index === highlightedIndex
            return (
              <button
                key={`${String(opt.value)}-${index}`}
                type="button"
                role="option"
                tabIndex={-1}
                id={`${listboxId}-opt-${index}`}
                data-option-index={index}
                aria-selected={selected}
                disabled={opt.disabled}
                className={`${styles.option} ${selected ? styles.optionSelected : ''} ${highlighted ? styles.optionHighlighted : ''}`}
                onMouseEnter={() => setHighlightedIndex(index)}
                onClick={() => selectIndex(index)}
              >
                {opt.label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default Select
